import "./styles.min.css";

const SECTION_SELECTOR = "[data-swiper-instance]";
const PRIORITY_IMMEDIATE = "immediate";
const DEFAULT_PRIORITY = "lazy";

const loaders = {
  news: () =>
    import(/* webpackChunkName: "swiper-news" */ "./entries/news.jsx"),
  videos: () =>
    import(/* webpackChunkName: "swiper-videos" */ "./entries/videos.jsx"),
  quotes: () =>
    import(/* webpackChunkName: "swiper-quotes" */ "./entries/quotes.jsx"),
};

const registeredSections = new WeakSet();
let observer;

function hydrateSection(section) {
  if (!section || section.dataset.swiperHydrated === "true") {
    return;
  }

  const instance = section.dataset.swiperInstance;
  const loader = loaders[instance];

  if (typeof loader !== "function") {
    console.warn(`No Swiper loader registered for type: ${instance}`);
    return;
  }

  section.dataset.swiperHydrated = "pending";

  loader()
    .then((module) => {
      if (typeof module.mount !== "function") {
        throw new Error(`Mount function missing for ${instance}`);
      }

      module.mount(section);
      section.dataset.swiperHydrated = "true";
    })
    .catch((error) => {
      console.error(`Failed to initialize Swiper instance: ${instance}`, error);
      section.dataset.swiperHydrated = "error";
    });
}

function ensureObserver() {
  if (observer || typeof IntersectionObserver === "undefined") {
    return observer;
  }

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          hydrateSection(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: "180px 0px",
      threshold: 0.1,
    }
  );

  return observer;
}

function registerSection(section) {
  if (!section || registeredSections.has(section)) {
    return;
  }

  registeredSections.add(section);
  const priority = section.dataset.swiperPriority || DEFAULT_PRIORITY;

  if (
    priority === PRIORITY_IMMEDIATE ||
    typeof IntersectionObserver === "undefined"
  ) {
    hydrateSection(section);
    return;
  }

  ensureObserver().observe(section);
}

function scanForSections(root = document) {
  const sections = root.querySelectorAll(SECTION_SELECTOR);
  sections.forEach(registerSection);
}

function watchDomMutations() {
  if (typeof MutationObserver === "undefined") {
    return;
  }

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        if (node.matches?.(SECTION_SELECTOR)) {
          registerSection(node);
        } else {
          scanForSections(node);
        }
      });
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function bootstrap() {
  scanForSections(document);
  watchDomMutations();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootstrap);
} else {
  bootstrap();
}
